import type { SidebarGroup } from "../../domain/app-types.ts";
import type { AppView } from "../../app/shell-view.ts";

export interface SidebarMenuState {
  kind: "space" | "category";
  id: string;
}

export interface SidebarInteractionCapabilities {
  document: Document;
  activeView(): AppView;
  activeSavedView(): string | null;
  expandedGroup(): SidebarGroup | null;
  groupSelectionMade?(): boolean;
  expandedSpace(): string | null;
  openMenu(): SidebarMenuState | null;
  setOpenMenu(value: SidebarMenuState | null): void;
}

function currentSidebarGroup(group: SidebarGroup | null): SidebarGroup | null {
  if (group === "saved") return "shortcuts";
  if (group === "templates" || group === "planning") return "tools";
  return group;
}

export function effectiveSidebarGroup(activeView: AppView, activeSavedView: string | null, expandedGroup: SidebarGroup | null, groupSelectionMade = false): SidebarGroup | null {
  if (groupSelectionMade) return currentSidebarGroup(expandedGroup);
  if (activeSavedView) return "shortcuts";
  if (activeView === "cycle" || activeView === "backlog") return "tools";
  if (["today", "scheduled", "upcoming", "completed", "archived", "conflicts", "trash"].includes(activeView)) return "more";
  return currentSidebarGroup(expandedGroup);
}

export function createSidebarInteractionController(capabilities: SidebarInteractionCapabilities) {
  function currentGroup(): SidebarGroup | null {
    return effectiveSidebarGroup(capabilities.activeView(), capabilities.activeSavedView(), capabilities.expandedGroup(), capabilities.groupSelectionMade?.());
  }

  function updateGroupDisclosures(): void {
    const expanded = currentGroup();
    capabilities.document.querySelectorAll<HTMLElement>("[data-action=toggle-sidebar-group]").forEach(button => {
      const isExpanded = button.dataset.group === expanded;
      button.setAttribute("aria-expanded", String(isExpanded));
      button.classList.toggle("is-expanded", isExpanded);
    });
    capabilities.document.querySelectorAll<HTMLElement>("[id^=sidebar-]").forEach(content => {
      content.hidden = content.id !== `sidebar-${expanded}`;
    });
  }

  function updateSpaceDisclosures(): void {
    const expanded = capabilities.expandedSpace();
    capabilities.document.querySelectorAll<HTMLElement>("[data-action=toggle-space]").forEach(button => {
      const isExpanded = button.dataset.id === expanded;
      button.setAttribute("aria-expanded", String(isExpanded));
      button.setAttribute("aria-label", `${isExpanded ? "Collapse" : "Expand"} ${button.dataset.label || "space"}`);
    });
    capabilities.document.querySelectorAll<HTMLElement>(".space-children").forEach(content => {
      content.hidden = content.id !== `space-children-${expanded}`;
    });
  }

  function closeMenu(restoreFocus = false): void {
    const menu = capabilities.openMenu();
    capabilities.setOpenMenu(null);
    capabilities.document.querySelectorAll<HTMLElement>(".sidebar-action-menu").forEach(element => { element.hidden = true; element.classList.remove("is-positioned"); });
    capabilities.document.querySelector<HTMLElement>("#sidebar")?.classList.remove("has-open-menu");
    capabilities.document.querySelectorAll<HTMLElement>("[data-action=toggle-sidebar-menu]").forEach(button => button.setAttribute("aria-expanded", "false"));
    if (restoreFocus && menu) {
      capabilities.document.querySelector<HTMLElement>(`[data-action="toggle-sidebar-menu"][data-kind="${menu.kind}"][data-id="${menu.id}"]`)?.focus();
    }
  }

  return { currentGroup, updateGroupDisclosures, updateSpaceDisclosures, closeMenu };
}
